import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { validateEmail, validatePhone, validateGSTNumber } from '../utils/helpers';
import { indianStates } from '../utils/indianStates';
import { 
    UserPlus, 
    Pencil, 
    X
} from 'lucide-react';

const CustomerModal = ({ isOpen, onClose, onSubmit, initialData = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    gstNumber: '',
    placeOfSupply: ''
  });

  const [errors, setErrors] = useState({});

  // Update form data when initialData changes (for editing)
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      // Reset form when creating new customer
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        gstNumber: '',
        placeOfSupply: ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, isOpen]);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Auto-fill City and State from Pincode
    if (name === 'pincode' && value.length === 6) {
      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${value}`);
        const data = await response.json();
        if (data && data[0].Status === 'Success') {
          const postOffice = data[0].PostOffice[0];
          setFormData(prev => ({
            ...prev,
            city: postOffice.District || postOffice.Block || prev.city,
            state: postOffice.State || prev.state,
            placeOfSupply: postOffice.State || prev.placeOfSupply
          }));
        }
      } catch (err) {
        console.error('Failed to fetch pincode details', err);
      }
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.phone) {
      newErrors.phone = 'Phone is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Invalid phone number (10 digits required)';
    }

    if (!formData.placeOfSupply) {
      newErrors.placeOfSupply = 'Place of supply is required';
    }

    if (formData.gstNumber && !validateGSTNumber(formData.gstNumber)) {
      newErrors.gstNumber = 'Invalid GST number format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Edit Customer' : 'Add Customer'}
      footer={
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <X size={18} /> Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            onClick={handleSubmit}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            {initialData ? <Pencil size={18} /> : <UserPlus size={18} />}
            {initialData ? 'Update' : 'Create'}
          </button>
        </div>
      }
    >
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">Name *</label>
          <input
            type="text"
            name="name"
            className="form-control"
            value={formData.name}
            onChange={handleChange}
          />
          {errors.name && <span style={{ color: 'red', fontSize: '12px' }}>{errors.name}</span>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              name="email"
              className="form-control"
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && <span style={{ color: 'red', fontSize: '12px' }}>{errors.email}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Phone *</label>
            <input
              type="text"
              name="phone"
              className="form-control"
              value={formData.phone}
              onChange={handleChange}
              maxLength="10"
            />
            {errors.phone && <span style={{ color: 'red', fontSize: '12px' }}>{errors.phone}</span>}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Address</label>
          <textarea
            name="address"
            className="form-control"
            value={formData.address}
            onChange={handleChange}
            rows="2"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Pincode</label>
            <input
              type="text"
              name="pincode"
              className="form-control"
              value={formData.pincode}
              onChange={handleChange}
              maxLength="6"
            />
          </div>

          <div className="form-group">
            <label className="form-label">City</label>
            <input
              type="text"
              name="city"
              className="form-control"
              value={formData.city}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label">State</label>
            <select
              name="state"
              className="form-control"
              value={formData.state}
              onChange={handleChange}
            >
              <option value="">Select State</option>
              {indianStates.map(state => (
                <option key={state.code} value={state.name}>
                  {state.code} - {state.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">GST Number (Optional)</label>
            <input
              type="text"
              name="gstNumber"
              className="form-control"
              value={formData.gstNumber}
              onChange={handleChange}
              maxLength="15"
              style={{ textTransform: 'uppercase' }}
            />
            {errors.gstNumber && <span style={{ color: 'red', fontSize: '12px' }}>{errors.gstNumber}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Place of Supply *</label>
            <select
              name="placeOfSupply"
              className="form-control"
              value={formData.placeOfSupply}
              onChange={handleChange}
            >
              <option value="">Select Place of Supply</option>
              {indianStates.map(state => (
                <option key={state.code} value={state.name}>
                  {state.code} - {state.name}
                </option>
              ))}
            </select>
            {errors.placeOfSupply && <span style={{ color: 'red', fontSize: '12px' }}>{errors.placeOfSupply}</span>}
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default CustomerModal;
